import { useState } from 'react';
import { Segment, Grid, Icon, Divider, Header, Form, Button, Message, Select } from 'semantic-ui-react';
import { getCommonAPI } from 'hpp-fe-common';

const COUNTRIES = [
    { key: 'lk', text: 'Sri Lanka', value: '000000000000000000000001' },
    { key: 'bd', text: 'Bangladesh', value: '000000000000000000000002' },
    { key: 'mv', text: 'Maldives', value: '000000000000000000000003' },
    { key: 'np', text: 'Nepal', value: '000000000000000000000004' },
    { key: 'pk', text: 'Pakistan', value: '000000000000000000000005' },
];

function SerialVerify() {
    const [countryId, setCountryId] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleSearch = async () => {
        if (!countryId || !serialNumber.trim()) return;
        setLoading(true);
        setResult(null);
        setError('');
        try {
            const { data } = await getCommonAPI().get(
                `/api_v1/verify/check-serial?serialNumber=${encodeURIComponent(serialNumber.trim())}&countryId=${countryId}`
            );
            if (data.error) {
                setError(data.errorMessage);
            } else {
                setResult({ isGenuine: data.data.isGenuine, serialSearched: serialNumber.trim().toUpperCase() });
            }
        } catch (err) {
            if (err.response?.data?.errorMessage) {
                setError(err.response.data.errorMessage);
            } else if (err.response?.status === 429) {
                setError('Too many requests. Please wait a moment and try again.');
            } else {
                setError('Unable to connect. Please check your connection and try again.');
            }
        }
        setLoading(false);
    };

    const handleReset = () => {
        setResult(null);
        setSerialNumber('');
        setError('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    return (
        <Segment
            raised
            style={{
                maxWidth: '480px',
                margin: 'auto',
                padding: '0',
                borderRadius: '12px',
                backgroundColor: '#fff',
                boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                borderTop: '4px solid #C8102E'
            }}
        >
            {/* Header Section */}
            <div style={{ padding: '28px 30px 20px', textAlign: 'center' }}>
                <Grid textAlign="center">
                    <Grid.Row>
                        <Header
                            as="h2"
                            style={{
                                color: '#2c3e50',
                                fontWeight: '700',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                margin: 0
                            }}
                        >
                            <Icon name="shield" color="red" size="large" />
                            <span style={{ marginTop: '10px' }}>Verify Device Authenticity</span>
                        </Header>
                        <p style={{ fontSize: '14px', color: '#666', marginTop: '10px', lineHeight: '1.6' }}>
                            Check if your Hikvision product is{' '}
                            <strong style={{ color: '#C8102E' }}>genuine and authorized</strong>
                        </p>
                    </Grid.Row>
                </Grid>
            </div>

            <Divider style={{ borderTop: '2px solid #C8102E', margin: '0' }} />

            {/* Form / Result Section */}
            <div style={{ padding: '24px 30px 28px', textAlign: 'left' }}>

                {!result && (
                    <>
                        <div style={{
                            backgroundColor: '#f0f7ff',
                            border: '1px solid #bee3f8',
                            borderLeft: '4px solid #3182ce',
                            borderRadius: '6px',
                            padding: '10px 14px',
                            marginBottom: '16px',
                            fontSize: '13px',
                            color: '#2c5282',
                            lineHeight: '1.6',
                            textAlign: 'left'
                        }}>
                            <Icon name="info circle" style={{ color: '#3182ce' }} />
                            You can verify serials manufactured from <strong>1st January 2023</strong> onwards.
                            For earlier manufactured serials, please contact our team at{' '}
                            <a href="mailto:support@hikvision.lk" style={{ color: '#3182ce' }}>support@hikvision.lk</a> to check genuinity.
                        </div>
                        <Form>

                        <Form.Field>
                            <label style={{ color: '#2c3e50', fontWeight: '600' }}>Country</label>
                            <Select
                                placeholder="Select your country"
                                options={COUNTRIES}
                                value={countryId}
                                onChange={(e, { value }) => { setCountryId(value); setError(''); }}
                                fluid
                            />
                        </Form.Field>

                        <Form.Field style={{ marginTop: '16px' }}>
                            <label style={{ color: '#2c3e50', fontWeight: '600' }}>Serial Number</label>
                            <Form.Input
                                placeholder="Enter serial number"
                                value={serialNumber}
                                onChange={(e) => { setSerialNumber(e.target.value); setError(''); }}
                                onKeyDown={handleKeyDown}
                                disabled={!countryId}
                                autoComplete="off"
                                maxLength={32}
                            />
                        </Form.Field>

                        {error && (
                            <Message negative size="small" style={{ marginTop: '12px' }}>
                                <p>{error}</p>
                            </Message>
                        )}

                        <Button
                            fluid
                            style={{
                                marginTop: '20px',
                                backgroundColor: (!countryId || !serialNumber.trim() || loading) ? '#e8b4b8' : '#C8102E',
                                color: '#fff',
                                fontWeight: '600',
                                letterSpacing: '0.3px'
                            }}
                            loading={loading}
                            disabled={!countryId || !serialNumber.trim() || loading}
                            onClick={handleSearch}
                        >
                            <Icon name="search" />
                            Check Serial
                        </Button>
                    </Form>
                    </>
                )}

                {result && (
                    <Grid textAlign="center">
                        <Grid.Row>
                            {result.isGenuine ? (
                                <>
                                    <Header
                                        as="h2"
                                        style={{
                                            color: '#21ba45',
                                            fontWeight: '700',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            marginTop: '10px'
                                        }}
                                    >
                                        <Icon name="check circle" color="green" size="large" />
                                        <span style={{ marginTop: '10px' }}>Original Authorized Device</span>
                                    </Header>
                                    <p style={{ fontSize: '14px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                                        Serial <strong style={{ color: '#C8102E' }}>{result.serialSearched}</strong> is
                                        registered in the <strong>Hikvision authorized distribution network</strong>.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <Header
                                        as="h2"
                                        style={{
                                            color: '#db2828',
                                            fontWeight: '700',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            marginTop: '10px'
                                        }}
                                    >
                                        <Icon name="times circle" color="red" size="large" />
                                        <span style={{ marginTop: '10px' }}>Not a Registered Device</span>
                                    </Header>
                                    <p style={{ fontSize: '14px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                                        Serial <strong style={{ color: '#C8102E' }}>{result.serialSearched}</strong> was
                                        not found in our system. Please contact your authorized dealer.
                                    </p>
                                </>
                            )}
                        </Grid.Row>

                        <Grid.Row>
                            <Button
                                basic
                                style={{ color: '#C8102E', borderColor: '#C8102E', marginTop: '8px' }}
                                onClick={handleReset}
                            >
                                <Icon name="redo" />
                                Check Another
                            </Button>
                        </Grid.Row>
                    </Grid>
                )}

            </div>
        </Segment>
    );
}

export default SerialVerify;
